import Box from "@mui/material/Box";
import {
  BALANCE_AFTER,
  BUTTON_CONNECTED,
  BUTTON_DISCONNECTED,
  BYOK_DEFAULT_PAYMENT_MODE_LABEL,
  BYOK_PAYMENT_MODE_BYOK,
  BYOK_PAYMENT_MODE_CREDITS,
  BYOK_CONFIGURE_MISSING_PROVIDERS,
  BYOK_PROVIDERS_WILL_BE_USED,
  COST_OF_ACTION,
  CURRENT_BALANCE,
  PRICING_CREDIT_USAGE_CREDIT,
  PRICING_CREDIT_USAGE_CREDITS,
  VIDEO,
  VIDEOS,
} from "../../i18n/keysTranslations";
import Typography from "@mui/material/Typography";
import { useTranslationApp } from "../../lib/i18next";
import { useSelectorApp } from "../../lib/redux";
import {
  ADS_ACTION_TYPES,
  BORDER_RADIUS,
  PLAN_TYPE_CREDITS,
} from "../../utils/constants";
import {
  getByokProvidersForActionType,
  isByokConfirmBlocked,
  isByokProviderConfigured,
} from "../../utils/byokProviders";
import { setSwalConfirmButtonDisabled } from "../Alerts/Alerts";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { Coins, Video } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ModalApiKeys from "./ModalApiKeys";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";
import { useForm } from "react-hook-form";
import Select, { SELECT_MENU_ABOVE_MODAL_PROPS } from "../Form/Select";
import { AlertPortalProviders } from "../Containers/ThemeContainer";

const PAYMENT_MODE_OPTIONS = [
  { value: "credits", label: BYOK_PAYMENT_MODE_CREDITS },
  { value: "byok", label: BYOK_PAYMENT_MODE_BYOK },
];

const OPTIONAL_FIELD_RULES = { required: false };

const resolveUnitLabelKey = ({ meterKey, value }) => {
  const n = Number(value);
  const isOne = n === 1;

  if (meterKey === ADS_ACTION_TYPES.VIDEOS) {
    return isOne ? VIDEO : VIDEOS;
  }

  return isOne ? PRICING_CREDIT_USAGE_CREDIT : PRICING_CREDIT_USAGE_CREDITS;
};

const UsageInfoRow = ({ labelKey, value, meterKey, Icon }) => {
  const { t } = useTranslationApp();
  const unitLabelKey = resolveUnitLabelKey({ meterKey, value });

  const labelStyles = {
    fontWeight:
      labelKey === BALANCE_AFTER || labelKey === COST_OF_ACTION ? 600 : 500,
    color: "text.primary",
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography
        variant="body2"
        sx={{
          ...labelStyles,
        }}
      >
        {t(labelKey)}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <Icon size={16} />
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {t(unitLabelKey)}
        </Typography>
      </Box>
    </Box>
  );
};

const ByokProviderRow = ({ provider, configured, t }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: `${BORDER_RADIUS}px`,
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={provider.logo}
          alt={provider.label}
          sx={{ width: 18, height: 18, objectFit: "contain" }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: "text.primary" }}
      >
        {provider.label}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: configured ? "#22c55e" : "text.disabled",
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: configured ? "text.primary" : "text.secondary",
          fontWeight: 500,
        }}
      >
        {t(configured ? BUTTON_CONNECTED : BUTTON_DISCONNECTED)}
      </Typography>
    </Box>
  </Box>
);

const ByokProvidersSection = ({ actionType, t }) => {
  const [isApiKeysModalOpen, setIsApiKeysModalOpen] = useState(false);
  const configuredProviders = useSelectorApp(
    (state) => state.payments?.byokConfigurationPublic?.providers || {}
  );

  const providersForAction = useMemo(
    () => getByokProvidersForActionType(actionType),
    [actionType]
  );

  const hasUnconfiguredProviders = useMemo(
    () =>
      providersForAction.some(
        (provider) =>
          !isByokProviderConfigured(provider.id, configuredProviders)
      ),
    [providersForAction, configuredProviders]
  );

  if (!providersForAction.length) return null;

  return (
    <>
      <Box
        sx={{
          mt: 2,
          bgcolor: "#fafafa",
          borderRadius: 2,
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, color: "text.primary" }}
        >
          {t(BYOK_PROVIDERS_WILL_BE_USED)}
        </Typography>
        {providersForAction.map((provider) => (
          <ByokProviderRow
            key={provider.id}
            provider={provider}
            configured={isByokProviderConfigured(
              provider.id,
              configuredProviders
            )}
            t={t}
          />
        ))}
        {hasUnconfiguredProviders ? (
          <Typography
            variant="body2"
            component="button"
            type="button"
            onClick={() => setIsApiKeysModalOpen(true)}
            sx={{
              alignSelf: "flex-start",
              mt: 0.5,
              p: 0,
              border: "none",
              background: "none",
              font: "inherit",
              color: "text.primary",
              textDecoration: "underline",
              cursor: "pointer",
              "&:hover": {
                color: "text.secondary",
              },
            }}
          >
            {t(BYOK_CONFIGURE_MISSING_PROVIDERS)}
          </Typography>
        ) : null}
      </Box>
      <ModalApiKeys
        modalOpen={isApiKeysModalOpen}
        onCloseModal={() => setIsApiKeysModalOpen(false)}
      />
    </>
  );
};

const ConfirmUsageContent = ({
  typeOfPlans,
  actionType,
  meterKey = PLAN_TYPE_CREDITS,
  spend,
  credits,
  lineItems = [],
  hideCreditsSummary = false,
  defaultPaymentMode = "credits",
  onChangePaymentMode = () => {},
  onConfirmBlockedChange = () => {},
}) => {
  const { t } = useTranslationApp();
  const { isAppSumo } = useAgencyIsAppSumo();
  const configuredProviders = useSelectorApp(
    (state) => state.payments?.byokConfigurationPublic?.providers || {}
  );
  const remaining = credits - spend;
  const isCreditsMeter = meterKey === PLAN_TYPE_CREDITS;
  const isVideosMeter = meterKey === ADS_ACTION_TYPES.VIDEOS;
  const visibleLineItems = useMemo(
    () =>
      (Array.isArray(lineItems) ? lineItems : []).filter(
        (item) => item?.labelKey && Number(item.amount) > 0
      ),
    [lineItems]
  );
  const showLineItems = visibleLineItems.length > 1;

  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      defaultPaymentMode,
    },
  });

  const selectedPaymentMode = watch("defaultPaymentMode");
  const effectivePaymentMode = isAppSumo ? selectedPaymentMode : "credits";

  useEffect(() => {
    setValue("defaultPaymentMode", defaultPaymentMode);
  }, [defaultPaymentMode, setValue]);

  const showPaymentModeSelector = isAppSumo;

  const showCreditsSummary =
    !hideCreditsSummary &&
    effectivePaymentMode !== "byok" &&
    typeOfPlans === PLAN_TYPE_CREDITS &&
    (isCreditsMeter || isVideosMeter);

  const showByokProviders =
    isAppSumo && effectivePaymentMode === "byok" && Boolean(actionType);

  const isByokProvidersBlocked = useMemo(() => {
    if (!isAppSumo || effectivePaymentMode !== "byok") return false;

    return isByokConfirmBlocked({
      paymentMode: effectivePaymentMode,
      actionType,
      configuredProviders,
    });
  }, [isAppSumo, effectivePaymentMode, actionType, configuredProviders]);

  useEffect(() => {
    if (!isAppSumo) {
      setSwalConfirmButtonDisabled(false);
      onConfirmBlockedChange(false);
      return;
    }

    setSwalConfirmButtonDisabled(isByokProvidersBlocked);
    onConfirmBlockedChange(isByokProvidersBlocked);
  }, [isAppSumo, isByokProvidersBlocked, onConfirmBlockedChange]);

  useEffect(
    () => () => {
      setSwalConfirmButtonDisabled(false);
      onConfirmBlockedChange(false);
    },
    [onConfirmBlockedChange]
  );

  return (
    <Box sx={{ px: 3 }}>
      {showPaymentModeSelector ? (
        <Box
          sx={{
            mb: showCreditsSummary || showByokProviders ? 2 : 0,
          }}
        >
          <Select
            control={control}
            name="defaultPaymentMode"
            label={t(BYOK_DEFAULT_PAYMENT_MODE_LABEL)}
            options={PAYMENT_MODE_OPTIONS}
            doTranslate
            rules={OPTIONAL_FIELD_RULES}
            errors={errors}
            fullWidth
            variant="filled"
            MenuProps={SELECT_MENU_ABOVE_MODAL_PROPS}
            actionsChange={onChangePaymentMode}
          />
        </Box>
      ) : null}

      {showByokProviders ? (
        <ByokProvidersSection actionType={actionType} t={t} />
      ) : null}
      {showCreditsSummary ? (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Box
            sx={{
              bgcolor: "#fafafa",
              borderRadius: 2,
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {showLineItems
              ? visibleLineItems.map((item) => (
                  <UsageInfoRow
                    key={item.labelKey}
                    labelKey={item.labelKey}
                    value={item.amount}
                    meterKey={meterKey}
                    Icon={isVideosMeter ? Video : Coins}
                  />
                ))
              : null}
            <UsageInfoRow
              labelKey={COST_OF_ACTION}
              value={spend}
              meterKey={meterKey}
              Icon={isVideosMeter ? Video : Coins}
            />
            <UsageInfoRow
              labelKey={CURRENT_BALANCE}
              value={credits}
              meterKey={meterKey}
              Icon={isVideosMeter ? Video : Coins}
            />
            {remaining >= 0 ? (
              <UsageInfoRow
                labelKey={BALANCE_AFTER}
                value={remaining}
                meterKey={meterKey}
                Icon={isVideosMeter ? Video : Coins}
              />
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

const ConfirmUsage = (props) => (
  <AlertPortalProviders>
    <ConfirmUsageContent {...props} />
  </AlertPortalProviders>
);

export default ConfirmUsage;
